import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/common/Alert';
import { FileText, Save, DollarSign, MapPin, CheckCircle, AlertTriangle, RefreshCw } from 'lucide-react';

const formatDollars = (cents) => `$${(cents / 100).toFixed(2)}`;

function TaxCodeConfigSection() {
  const [taxCodes, setTaxCodes] = useState([]);
  const [stripeProducts, setStripeProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRow, setEditingRow] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saveStatus, setSaveStatus] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [codesRes, productsRes] = await Promise.all([
        apiRequest('/api/v1/admin/accounting/tax-codes'),
        apiRequest('/api/v1/admin/accounting/stripe-tax-products')
      ]);
      if (codesRes.success) {
        setTaxCodes(codesRes.data);
      }
      if (productsRes.success) {
        setStripeProducts(productsRes.data);
      }
    } catch (err) {
      setSaveStatus({ type: 'error', message: 'Failed to load tax codes' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleEdit = (activityType, currentCode) => {
    setEditingRow(activityType);
    setEditValue(currentCode || '');
    setSaveStatus(null);
  };

  const handleSave = async (activityType) => {
    try {
      const res = await apiRequest(`/api/v1/admin/accounting/tax-codes/${activityType}`, {
        method: 'PATCH',
        body: JSON.stringify({ stripe_tax_code: editValue || null })
      });
      if (res.success) {
        setTaxCodes(prev => prev.map(tc =>
          tc.activity_type === activityType ? res.data : tc
        ));
        setSaveStatus({ type: 'success', message: `Updated ${activityType}` });
        setEditingRow(null);
      }
    } catch (err) {
      setSaveStatus({ type: 'error', message: `Failed to update ${activityType}` });
    }
  };

  const handleCancel = () => {
    setEditingRow(null);
    setEditValue('');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div style={{ marginBottom: '2rem' }}>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <FileText size={20} /> Tax Code Configuration
      </h2>
      {saveStatus && <Alert message={saveStatus.message} type={saveStatus.type} />}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
              <th style={{ padding: '0.75rem', fontWeight: 600 }}>Activity Type</th>
              <th style={{ padding: '0.75rem', fontWeight: 600 }}>Stripe Tax Code</th>
              <th style={{ padding: '0.75rem', fontWeight: 600 }}>Description</th>
              <th style={{ padding: '0.75rem', fontWeight: 600 }}>Active</th>
              <th style={{ padding: '0.75rem', fontWeight: 600 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {taxCodes.map(tc => (
              <tr key={tc.activity_type} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '0.75rem', textTransform: 'capitalize' }}>{tc.activity_type.replace(/_/g, ' ')}</td>
                <td style={{ padding: '0.75rem' }}>
                  {editingRow === tc.activity_type ? (
                    <select
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      style={{ padding: '0.375rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', width: '100%', maxWidth: '300px' }}
                    >
                      <option value="">No Exemption (Fully Taxable)</option>
                      {stripeProducts.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ color: tc.stripe_tax_code ? '#111827' : '#9ca3af', fontStyle: tc.stripe_tax_code ? 'normal' : 'italic' }}>
                      {tc.stripe_tax_code || 'No Exemption (Fully Taxable)'}
                    </span>
                  )}
                </td>
                <td style={{ padding: '0.75rem', color: '#6b7280' }}>{tc.description}</td>
                <td style={{ padding: '0.75rem' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '0.125rem 0.5rem',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    backgroundColor: tc.taxable ? '#d1fae5' : '#fee2e2',
                    color: tc.taxable ? '#065f46' : '#991b1b'
                  }}>
                    {tc.taxable ? 'Yes' : 'No'}
                  </span>
                </td>
                <td style={{ padding: '0.75rem' }}>
                  {editingRow === tc.activity_type ? (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => handleSave(tc.activity_type)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.25rem',
                          padding: '0.375rem 0.75rem', backgroundColor: '#059669', color: 'white',
                          border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8125rem'
                        }}
                      >
                        <Save size={14} /> Save
                      </button>
                      <button
                        onClick={handleCancel}
                        style={{
                          padding: '0.375rem 0.75rem', backgroundColor: '#f3f4f6', color: '#374151',
                          border: '1px solid #d1d5db', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8125rem'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleEdit(tc.activity_type, tc.stripe_tax_code)}
                      style={{
                        padding: '0.375rem 0.75rem', backgroundColor: '#f3f4f6', color: '#374151',
                        border: '1px solid #d1d5db', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8125rem'
                      }}
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TaxLiabilitySection() {
  const [liability, setLiability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiRequest('/api/v1/admin/accounting/tax-liability');
        if (res.success) {
          setLiability(res.data);
        }
      } catch (err) {
        setError('Failed to load tax liability data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div style={{ marginBottom: '2rem' }}>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <DollarSign size={20} /> Platform Tax Liability
      </h2>
      {error && <Alert message={error} type="error" />}
      {liability.length === 0 ? (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          color: '#6b7280',
          backgroundColor: '#f9fafb',
          borderRadius: '0.5rem',
          border: '1px solid #e5e7eb'
        }}>
          No outstanding tax liability.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: '0.75rem', fontWeight: 600 }}>State</th>
                <th style={{ padding: '0.75rem', fontWeight: 600 }}>Balance</th>
                <th style={{ padding: '0.75rem', fontWeight: 600 }}>Current Period</th>
                <th style={{ padding: '0.75rem', fontWeight: 600 }}>Last Swept</th>
              </tr>
            </thead>
            <tbody>
              {liability.map(row => (
                <tr key={row.state} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <MapPin size={14} style={{ color: '#6b7280' }} /> {row.state}
                  </td>
                  <td style={{ padding: '0.75rem', fontWeight: 600 }}>{formatDollars(row.balance_cents)}</td>
                  <td style={{ padding: '0.75rem' }}>{row.current_period}</td>
                  <td style={{ padding: '0.75rem', color: row.last_swept_at ? '#111827' : '#9ca3af' }}>
                    {row.last_swept_at ? new Date(row.last_swept_at).toLocaleDateString() : 'Never'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ReconciliationSection() {
  const now = new Date();
  const defaultPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [period, setPeriod] = useState(defaultPeriod);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchReconciliation = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiRequest(`/api/v1/admin/accounting/tax-reconciliation?period=${period}`);
      if (res.success) {
        setData(res.data);
      }
    } catch (err) {
      setError('Failed to load reconciliation data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchReconciliation(); }, [fetchReconciliation]);

  return (
    <div style={{ marginBottom: '2rem' }}>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <RefreshCw size={20} /> Period Reconciliation
      </h2>
      <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>Period:</label>
        <input
          type="month"
          value={period}
          onChange={e => setPeriod(e.target.value)}
          style={{ padding: '0.375rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem' }}
        />
      </div>
      {error && <Alert message={error} type="error" />}
      {loading ? (
        <LoadingSpinner />
      ) : data && data.reconciliation ? (
        data.reconciliation.length === 0 ? (
          <div style={{
            padding: '2rem', textAlign: 'center', color: '#6b7280',
            backgroundColor: '#f9fafb', borderRadius: '0.5rem', border: '1px solid #e5e7eb'
          }}>
            No tax data for this period.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                  <th style={{ padding: '0.75rem', fontWeight: 600 }}>State</th>
                  <th style={{ padding: '0.75rem', fontWeight: 600 }}>Ledger Collected</th>
                  <th style={{ padding: '0.75rem', fontWeight: 600 }}>Stripe Reported</th>
                  <th style={{ padding: '0.75rem', fontWeight: 600 }}>Difference</th>
                  <th style={{ padding: '0.75rem', fontWeight: 600 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.reconciliation.map(row => (
                  <tr key={row.state} style={{
                    borderBottom: '1px solid #e5e7eb',
                    backgroundColor: row.status === 'warning' ? '#fffbeb' : 'transparent',
                  }}>
                    <td style={{ padding: '0.75rem' }}>{row.state}</td>
                    <td style={{ padding: '0.75rem' }}>{formatDollars(row.ledger_collected_cents)}</td>
                    <td style={{ padding: '0.75rem' }}>{formatDollars(row.stripe_reported_cents)}</td>
                    <td style={{ padding: '0.75rem', fontWeight: 600, color: row.difference_cents > 0 ? '#b45309' : '#065f46' }}>
                      {formatDollars(row.difference_cents)}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {row.status === 'clean' ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: '#065f46' }}>
                          <CheckCircle size={14} /> Clean
                        </span>
                      ) : (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: '#b45309' }}>
                          <AlertTriangle size={14} /> {row.warning}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </div>
  );
}

export default function AdminAccounting() {
  const navigate = useNavigate();
  const handleNavigate = (id, path) => { if (path) { navigate(path); } };
  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="accounting"
      onNavigate={handleNavigate}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>Accounting</h1>
        <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
          Manage tax code configuration and view platform tax liability.
        </p>

        <TaxCodeConfigSection />
        <TaxLiabilitySection />
        <ReconciliationSection />
      </div>
    </DashboardLayout>
  );
}
